import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { gql } from '@apollo/client';
import { formatCompactCurrency } from '../../../../utils/formatters';
import { partialSaleStatusDisplay } from '../../PartialSales/partialSaleStatus';
import { shouldShowPartialSaleAum } from '../../PartialSales/PartialSaleAumMetric';
import {
  buyerInterestState,
  canExpressInterest,
  INTEREST_PENDING,
  INTEREST_APPROVED,
} from '../../PartialSales/buyerInterestState';
import Button from '../../../../../../ui/canon/Button';
import Modal from '../../../../../../ui/canon/Modal';

// Same mutation the marketplace uses, so both entry points go through one flow.
const EXPRESS_INTEREST_FOR_PARTIAL_SALE = gql`
  mutation expressInterestForPartialSale($partialSaleId: ID!, $requestMessage: String!) {
    expressInterestForPartialSale(partialSaleId: $partialSaleId, requestMessage: $requestMessage) {
      id
      segment_details
    }
  }
`;

/**
 * Partial Sale Listings on a user's profile drawer.
 *
 * Card layout mirrors the marketplace card (SuccessionPartialSalesMarketplace) —
 * same status vocabulary, same metric set, same compact currency — at drawer
 * scale. The seller's identity is already the subject of the drawer, so the
 * card's anchor is the segment name rather than the person.
 */
const PartialSalesSection = ({ user, company, viewer, isViewingSelf, apolloClient }) => {
  const intl = useIntl();
  const t = (key) => intl.formatMessage({ id: `succession.profile_drawer.partial_sales.${key}` });
  // Interest vocabulary is shared with the marketplace so the two read identically.
  const m = (key, values) => intl.formatMessage({ id: `succession.partial_sales.marketplace.${key}` }, values);

  const [selectedListing, setSelectedListing] = useState(null);
  const [interestMessage, setInterestMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  // Interest submitted in this session, so the card flips to Pending without a refetch.
  const [locallyExpressed, setLocallyExpressed] = useState({});

  const formatNumber = (value) => {
    if (value === null || value === undefined || value === '') return '—';
    const num = typeof value === 'string' ? parseInt(value, 10) : value;
    return Number.isNaN(num) ? '—' : num.toLocaleString();
  };

  // Mirrors getTotalRevenue on the marketplace card: the sum of all four streams,
  // not just the two that used to be shown here.
  const totalRevenue = (listing) => (
    (parseFloat(listing.fee_based_revenue) || 0)
    + (parseFloat(listing.trails_revenue) || 0)
    + (parseFloat(listing.direct_revenue) || 0)
    + (parseFloat(listing.commission_revenue) || 0)
  );

  const isNegotiable = (price) => !price || parseFloat(price) === 0;
  // Same flag the marketplace card, wizard and edit form use to hide the asking price (RJ).
  const hideProductSalesPurchasePrice = company?.feature_types?.includes('hide_partial_sales_purchase_price') || false;

  const partialSaleListings = user.partial_sale_listings || [];
  const openPartialSaleListings = user.open_partial_sale_listings || [];

  // Combine both types of listings and remove duplicates based on ID
  const allListingsWithDuplicates = [...partialSaleListings, ...openPartialSaleListings];
  const allListings = allListingsWithDuplicates.filter((listing, index, self) =>
    index === self.findIndex((l) => l.id === listing.id)
  );

  if (allListings.length === 0) {
    return null;
  }

  const goals = user.transition_goal_names || [];

  const metric = (label, value) => (
    <div className="min-w-0">
      <div className="text-xs text-succession-text-muted mb-0.5 truncate">{label}</div>
      <div className="text-sm font-semibold text-succession-text-primary truncate">{value}</div>
    </div>
  );

  const breakdownRow = (label, value) => (
    <div className="flex items-baseline justify-between gap-2 min-w-0">
      <span className="text-xs text-succession-text-muted truncate">{label}</span>
      <span className="text-xs font-medium text-succession-text-secondary flex-shrink-0">{value}</span>
    </div>
  );

  const openInterestModal = (listing) => {
    setSelectedListing(listing);
    setSubmitError(null);
    setInterestMessage(m('interest_modal_default_message', {
      segment: listing.segment_details || '',
    }));
  };

  const closeInterestModal = () => {
    setSelectedListing(null);
    setInterestMessage('');
    setSubmitError(null);
  };

  const submitInterest = async () => {
    if (!selectedListing || !interestMessage.trim() || !apolloClient) return;
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await apolloClient.mutate({
        mutation: EXPRESS_INTEREST_FOR_PARTIAL_SALE,
        variables: {
          partialSaleId: selectedListing.id,
          requestMessage: interestMessage.trim(),
        },
      });
      // Flip this card to Pending straight away. The drawer does not refetch on
      // close, so without this the CTA would still be offered on a listing the
      // viewer has just expressed interest in.
      setLocallyExpressed((prev) => ({ ...prev, [selectedListing.id]: true }));
      closeInterestModal();
    } catch (error) {
      console.error('Error submitting interest:', error);
      setSubmitError(m('toast_interest_failed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-succession-bg-secondary rounded-lg border border-succession-border p-6">
      {/* The seller's transition goals describe the person, not any one listing, and
          this drawer is already about that person — so they sit once beside the
          heading rather than repeating identically on every card. */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 mb-4">
        <h3 className="text-xl font-semibold text-succession-text-primary">
          {t('partial_sale_listings')}
        </h3>
        {goals.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {goals.map((goal) => (
              <span
                key={goal}
                className="inline-flex px-2 py-0.5 text-xs rounded-full bg-succession-primary-10 text-succession-primary border border-succession-primary">
                {goal}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Two across at most. Three made every card narrow enough to truncate the
          segment name at common drawer widths. */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {allListings.map((listing, index) => {
          const status = partialSaleStatusDisplay(listing.sale_status);
          const showAum = shouldShowPartialSaleAum(listing, company);
          // Own listing: the drawer's subject is this user, so viewing your own
          // profile means every listing here is yours. The id check is belt and
          // braces for any caller that renders the section another way.
          const isOwnListing = isViewingSelf
            || (!!viewer?.id && String(listing.user?.id) === String(viewer.id));
          const justExpressed = !!locallyExpressed[listing.id];
          const interest = justExpressed
            ? INTEREST_PENDING
            : buyerInterestState(listing.current_user_interest_status);
          const offerCta = !justExpressed && canExpressInterest(listing, { isOwnListing });

          return (
            <div
              key={listing.id || index}
              className="min-w-0 bg-succession-bg-primary border border-succession-border rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow duration-300">
              {/* Status + asking price (hidden with hide_partial_sales_purchase_price) */}
              <div className="flex items-start justify-between gap-3 mb-3">
                <span className={`flex-shrink-0 inline-flex px-2 py-1 rounded-full text-xs font-medium ${status.className}`}>
                  {intl.formatMessage({ id: status.labelId })}
                </span>
                {!hideProductSalesPurchasePrice && (
                  <div className="text-right min-w-0">
                    <div className="text-xs text-succession-text-muted">{t('asking_price')}</div>
                    <div className="text-base font-bold text-succession-text-primary truncate">
                      {isNegotiable(listing.asking_price)
                        ? t('negotiable')
                        : formatCompactCurrency(listing.asking_price)}
                    </div>
                  </div>
                )}
              </div>

              {/* Segment name — the card's anchor, so it carries no label of its own */}
              {listing.segment_details && (
                <h4 className="text-base font-semibold text-succession-text-primary mb-1 break-words line-clamp-2">
                  {listing.segment_details}
                </h4>
              )}

              {/* Supporting prose, deliberately lighter than the name above it */}
              {listing.description && (
                <p className="text-sm text-succession-text-secondary mb-3 break-words line-clamp-2">
                  {listing.description}
                </p>
              )}

              {/* Headline metrics, matching the marketplace card's set */}
              <div className={`grid ${showAum ? 'grid-cols-3' : 'grid-cols-2'} gap-3 mb-3`}>
                {metric(t('clients_label'), formatNumber(listing.number_of_clients))}
                {metric(t('revenue_label'), formatCompactCurrency(totalRevenue(listing)))}
                {showAum && metric(t('aum_label'), formatCompactCurrency(listing.assets_under_management))}
              </div>

              {/* Revenue breakdown — kept so no figure shown before is lost */}
              <div className="space-y-1 pt-3 border-t border-succession-border">
                {breakdownRow(t('fee_based_revenue'), formatCompactCurrency(listing.fee_based_revenue || 0))}
                {breakdownRow(t('trails_revenue'), formatCompactCurrency(listing.trails_revenue || 0))}
              </div>

              {/* Your own interest: either the CTA, or where it already stands.
                  Nothing renders on your own listings or on non-live ones. */}
              {offerCta && (
                <div className="pt-3 mt-3 border-t border-succession-border">
                  <Button
                    onClick={() => openInterestModal(listing)}
                    variant="primary"
                    size="md"
                    className="w-full">
                    {m('express_interest')}
                  </Button>
                </div>
              )}

              {(interest === INTEREST_PENDING || interest === INTEREST_APPROVED) && (
                <div className="flex items-center justify-center gap-1 text-xs pt-3 mt-3 border-t border-succession-border">
                  <span className="text-succession-text-secondary">{m('view_status_label')}</span>
                  <span className="font-medium text-succession-primary">
                    {interest === INTEREST_APPROVED
                      ? m('interest_status_approved')
                      : m('interest_status_expressed')}
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Partial Sales Marketplace Link */}
      <div className="mt-4 bg-succession-primary/10 border border-succession-primary/20 rounded-lg p-4">
        <h5 className="text-sm font-semibold text-succession-text-primary mb-2">{t('interested_in_partial_sale')}</h5>
        <p className="text-sm text-succession-text-secondary mb-2">
          {t('visit_marketplace')}
        </p>
        <a
          href="/succession/partial_sales"
          className="text-succession-primary hover:underline text-sm font-medium">
          {t('go_to_marketplace')}
        </a>
      </div>

      {/* Express Interest — mirrors the marketplace modal, same copy and mutation.
          Portalled to the body because the drawer panel sets transform: translateX()
          for its slide-in (SuccessionProfileDrawerNew), which makes it the containing
          block for position: fixed descendants, and overflow-y-auto, which then clips
          them. Rendered inline the modal opens but is trapped inside the drawer.

          The stacking order is set with an inline style, not a z-[60] class: the
          drawer root is z-50, so the modal must beat it, and an arbitrary Tailwind
          z value is not reliably generated in this codebase (bg-[var(--…)] silently
          failed the same way). An inline style cannot be missed by the JIT. */}
      {typeof document !== 'undefined' && createPortal((
        <Modal
          open={!!selectedListing}
          onClose={closeInterestModal}
          overlayStyle={{ zIndex: 60 }}>
          <div className="p-6">
            <h2 className="text-xl font-semibold mb-4 text-succession-text-primary">
              {m('interest_modal_title')}
            </h2>
            <p className="text-sm mb-4 text-succession-text-secondary">
              {m('interest_modal_desc', { segment: selectedListing?.segment_details || '' })}
            </p>
            <textarea
              value={interestMessage}
              onChange={(e) => setInterestMessage(e.target.value)}
              className="w-full px-3 py-2 border border-succession-border rounded-lg focus:outline-none focus:ring-2 focus:ring-succession-focus-ring focus:border-transparent bg-succession-bg-secondary text-succession-text-primary"
              rows={4}
              placeholder={m('interest_modal_placeholder')}
            />
            {submitError && (
            <p className="text-sm text-succession-error mt-2" role="alert">{submitError}</p>
          )}
            <div className="flex justify-end space-x-3 mt-4">
              <Button onClick={closeInterestModal} variant="outline" size="md">
                {m('interest_modal_cancel')}
              </Button>
              <Button
                onClick={submitInterest}
                disabled={isSubmitting || !interestMessage.trim()}
                variant="primary"
                size="md">
                {isSubmitting ? m('interest_modal_sending') : m('interest_modal_send')}
              </Button>
            </div>
          </div>
        </Modal>
      ), document.body)}
    </div>
  );
};

PartialSalesSection.propTypes = {
  user: PropTypes.shape({
    partial_sale_listings: PropTypes.arrayOf(PropTypes.object),
    open_partial_sale_listings: PropTypes.arrayOf(PropTypes.object),
    transition_goal_names: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  // Used to gate the AUM metric to wealth tenants and to hide the asking price
  // (hide_partial_sales_purchase_price), exactly as the marketplace does.
  company: PropTypes.object,
  // The VIEWER (getCurrentUser), not the profile being viewed.
  viewer: PropTypes.shape({ id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]) }),
  isViewingSelf: PropTypes.bool,
  apolloClient: PropTypes.object,
};

PartialSalesSection.defaultProps = {
  company: null,
  viewer: null,
  isViewingSelf: false,
  apolloClient: null,
};

export default PartialSalesSection;
